import type { StateData } from '@/lib/become-an-agent-types';
import { highlightFacts } from '@/lib/highlight-facts';

/**
 * StateBreakdown
 *
 * Two-column block that reads as a continuation of StateFactsPanel above:
 * a Courtney Eisen pull-quote on the left, the market-data paragraph on the
 * right. Wrapped in hairline dividers (top + between + bottom) using the same
 * #F5F5F5/15 token as StateFactsPanel's footnote divider, so the two sections
 * feel like one tonal block.
 *
 * If a state is missing market_paragraph, the whole section is omitted.
 */
export function StateBreakdown({ state }: { state: StateData }) {
    if (!state.market_paragraph) return null;

    return (
        <section id="market" className="scroll-mt-20 bg-[#0e2727]">
            <div className="mx-auto max-w-6xl px-6">
                <div className="border-t border-[#F5F5F5]/15 py-10 sm:py-12 lg:py-14">
                    <div className="grid gap-10 md:grid-cols-2 md:gap-0">
                        {/* Left: Courtney Eisen pull-quote — editorial standfirst, no photo */}
                        <div className="md:pr-10 lg:pr-14">
                            <p className="lux-caps lux-caps-sm text-[#F5F5F5]/55">
                                From the Co-Founder
                            </p>
                            <blockquote
                                className="font-didot mt-4 text-[#E8E0D8] italic"
                                style={{
                                    fontSize: 'clamp(1.15rem, 1.5vw, 1.45rem)',
                                    lineHeight: 1.45,
                                    letterSpacing: '0.005em',
                                }}
                            >
                                &ldquo;Your earnings come down to two things: your network and your
                                ability to problem-solve. We give you the training and mentorship, the
                                tech and tools, and the rates to reach your full potential. Graduate from
                                our program and we open our network to yours.&rdquo;
                            </blockquote>
                            <cite className="lux-caps lux-caps-sm mt-5 block text-[#F5F5F5]/60 not-italic">
                                Courtney Eisen <span className="text-[#F5F5F5]/35">&middot;</span> Co-Founder &amp; CMO
                            </cite>
                            <a
                                href="/become-an-agent"
                                className="lux-caps lux-caps-sm mt-6 inline-block rounded-sm border border-obsidian bg-obsidian px-4 py-1.5 text-[#E8E0D8] transition-colors duration-300 hover:bg-[#0b2222]"
                            >
                                Learn more about the program
                            </a>
                            <p
                                className="mt-2 text-[10px] leading-snug text-[#E8E0D8]/70"
                                style={{ letterSpacing: '0.02em' }}
                            >
                                Free training. Innovative tech. Collaborative community.
                            </p>
                        </div>

                        {/* Right: market-data paragraph (vertical divider via border-l at md+) */}
                        <div className="md:border-l md:border-[#F5F5F5]/15 md:pl-10 lg:pl-14">
                            <h2
                                className="font-didot text-[#E8E0D8]"
                                style={{ fontSize: 'clamp(1.5rem, 2.2vw, 1.9rem)', lineHeight: 1.15 }}
                            >
                                How much do travel agents make in {state.state}?
                            </h2>
                            <p className="mt-5 leading-[1.75] text-[#E8E0D8]/85 [&_strong]:font-semibold [&_strong]:text-[#E8E0D8]">
                                {highlightFacts(state.market_paragraph)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
